// Stack tab on the Infrastructure page — every outside provider and every
// service we run, grouped by layer.
//
// Data: squish-crm-backend GET /api/infrastructure/stack, which serves
// lib/stack-catalog.json. Adding or retiring a provider is an edit there, not
// here. Status is hand-maintained for now; "In code" means wired in the code
// but not confirmed live. Items carry stable ids so live status reported by
// each service can be overlaid later without touching this layout.

import React, { useEffect, useMemo, useState } from 'react';
import { getHeaders } from '../api';

const API = import.meta.env.PROD ? '' : 'https://crm-api.squish.co';

const STATUS_LABEL = { live: 'Live', pilot: 'Pilot', build: 'In build', code: 'In code', planned: 'Planned' };
const STATUS_BADGE = { live: 'badge-green', pilot: 'badge-yellow', build: 'badge-blue', code: 'badge-gray', planned: 'badge-gray' };
const RECENT_DAYS = 30;

// "Cloudflare Workers" and "Cloudflare Pages" are Cloudflare; "Caddy + Let's
// Encrypt" counts once, as Caddy.
const company = vendor => vendor.replace(/ (Workers|Pages)$/, '').split(' + ')[0];

function isRecent(addedOn, now = Date.now()) {
  if (!addedOn) return false;
  const t = Date.parse(`${addedOn}T00:00:00Z`);
  return Number.isFinite(t) && now - t <= RECENT_DAYS * 86400000;
}

// "27 Sep" / "28 Sep 2026", spelled out so ICU never renders "Sept".
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
function formatDay(iso, withYear = false) {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso || '');
  if (!m) return '';
  const day = `${Number(m[3])} ${MONTHS[Number(m[2]) - 1]}`;
  return withYear ? `${day} ${m[1]}` : day;
}

function StackCard({ item, isOurs }) {
  const recent = isRecent(item.addedOn);
  return (
    <article className={`stack-card${recent ? ' is-new' : ''}`}>
      <div className="stack-card-top">
        <span className="stack-vendor">{item.vendor}</span>
        <span className="stack-pills">
          {recent && <span className="badge stack-new">NEW · {formatDay(item.addedOn)}</span>}
          <span className={`badge ${STATUS_BADGE[item.status] || 'badge-gray'}${item.status === 'code' ? ' stack-dashed' : ''}`}>
            {STATUS_LABEL[item.status] || item.status}
          </span>
        </span>
      </div>
      <div className={`stack-name${isOurs ? ' is-mono' : ''}`}>{item.name}</div>
      <div className="stack-role">{item.role}</div>
      <div className="stack-used">
        {item.usedBy.map(u => <span key={u}>{u}</span>)}
      </div>
    </article>
  );
}

export default function StackMap() {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState('all');
  const [q, setQ] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API}/api/infrastructure/stack`, { headers: getHeaders() });
        const json = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(json.error || `Failed to load the stack map (${res.status})`);
        if (!cancelled) setData(json);
      } catch (e) {
        if (!cancelled) setError(e.message);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const items = useMemo(() => data?.items || [], [data]);

  const summary = useMemo(() => {
    const inUse = new Set(items.filter(i => i.status !== 'planned').map(i => company(i.vendor)));
    const onDeck = new Set(items.filter(i => i.status === 'planned').map(i => company(i.vendor)));
    return {
      providers: inUse.size,
      ours: items.filter(i => i.layer === 'apps' && i.status !== 'planned').length,
      recent: items.filter(i => isRecent(i.addedOn)).length,
      onDeck: onDeck.size,
    };
  }, [items]);

  const filters = useMemo(() => {
    const count = f => f === 'all' ? items.length
      : f === 'recent' ? items.filter(i => isRecent(i.addedOn)).length
      : items.filter(i => i.status === f).length;
    return [
      { f: 'all', label: 'All' },
      { f: 'recent', label: 'Recently added' },
      ...(data?.statuses || []).map(s => ({ f: s, label: STATUS_LABEL[s] || s })),
    ].map(x => ({ ...x, n: count(x.f) })).filter(x => x.f === 'all' || x.n > 0);
  }, [items, data]);

  if (error) {
    return <div className="stack-error">{error}</div>;
  }
  if (!data) {
    return <div style={{ color: 'var(--text3)', fontSize: 13 }}>Loading the stack map…</div>;
  }

  const needle = q.trim().toLowerCase();
  const matches = i =>
    (filter === 'all' || (filter === 'recent' ? isRecent(i.addedOn) : i.status === filter)) &&
    (!needle || [i.vendor, i.name, i.role, ...i.usedBy].join(' ').toLowerCase().includes(needle));

  const layers = data.layers
    .map(l => ({ ...l, total: items.filter(i => i.layer === l.id).length, shown: items.filter(i => i.layer === l.id && matches(i)) }))
    .filter(l => l.shown.length > 0);

  return (
    <div className="stack-map">
      <div className="stack-summary">
        <span><strong>{summary.providers}</strong> providers in use</span>
        <span><strong>{summary.ours}</strong> services we run</span>
        <span className={summary.recent ? 'is-new' : ''}><strong>{summary.recent}</strong> added in the last {RECENT_DAYS} days</span>
        <span><strong>{summary.onDeck}</strong> on deck</span>
        <span className="stack-reviewed">Reviewed {formatDay(data.reviewedOn, true)}</span>
      </div>

      <div className="stack-controls">
        <div className="stack-filters" role="group" aria-label="Filter by status">
          {filters.map(x => (
            <button
              key={x.f}
              type="button"
              className={`stack-chip${filter === x.f ? ' is-active' : ''}${x.f === 'recent' ? ' is-recent' : ''}`}
              aria-pressed={filter === x.f}
              onClick={() => setFilter(x.f)}
            >
              {x.label}<span className="stack-chip-n">{x.n}</span>
            </button>
          ))}
        </div>
        <input
          id="stack-search"
          className="search-bar"
          type="search"
          placeholder="Search vendor, service, repo…"
          aria-label="Search the stack"
          value={q}
          onChange={e => setQ(e.target.value)}
        />
      </div>

      {layers.map(l => (
        <section key={l.id} className="stack-layer">
          <div className="stack-layer-head">
            <h2>{l.name}</h2>
            <p>{l.blurb}</p>
            <span className="stack-layer-count">
              {l.shown.length === l.total ? l.total : `${l.shown.length} of ${l.total}`}
            </span>
          </div>
          <div className="stack-grid">
            {l.shown.map(i => <StackCard key={i.id} item={i} isOurs={l.id === 'apps'} />)}
          </div>
        </section>
      ))}
      {layers.length === 0 && <div className="stack-empty">Nothing matches that filter.</div>}

      <div className="stack-lower">
        <section className="stack-box">
          <h3>Frameworks and runtimes</h3>
          {data.frameworks.map(f => (
            <div key={f.area} className="stack-row">
              <span className="stack-row-k">{f.area}</span>
              <span>{f.summary}</span>
            </div>
          ))}
        </section>
        <section className="stack-box">
          <h3>Retired or replaced</h3>
          {data.retired.map(r => (
            <div key={r.from} className="stack-row is-retired">
              <s>{r.from}</s>
              <span className="stack-row-to">{r.to}</span>
            </div>
          ))}
        </section>
      </div>

      <p className="stack-note">
        Status is kept by hand in <span className="stack-mono">squish-crm-backend/lib/stack-catalog.json</span>.
        {' '}<b>In code</b> means the integration is wired but nobody has confirmed it is live in production.
      </p>
    </div>
  );
}
